import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { AdminShell } from '../components/admin/AdminShell';
import {
  PageContainer,
  Card,
  CardBody,
  LoadingSpinner,
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  CloseButton,
  FormGroup,
  Label,
  Input,
  AlertMessage,
} from '../styles/adminShared';
import { adminApi } from '../services/adminApi';
import type { AdminSession } from '../services/adminApi';

// Page-specific styled components (profile-specific only)
const ProfileHeader = styled.div`
  background: linear-gradient(135deg, #1a365d 0%, #2c5282 100%);
  padding: 40px 24px;
  display: flex;
  align-items: center;
  gap: 24px;
  color: white;
`;

const AvatarLarge = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  font-weight: bold;
  border: 4px solid rgba(255, 255, 255, 0.3);
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const ProfileName = styled.h1`
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 600;
`;

const ProfileEmail = styled.p`
  margin: 0 0 12px 0;
  font-size: 16px;
  opacity: 0.9;
`;

const RoleBadge = styled.span`
  display: inline-block;
  padding: 6px 14px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Section = styled.div`
  margin-bottom: 32px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InfoLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.span`
  font-size: 15px;
  color: #1e293b;
`;

const MonoValue = styled.code`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 13px;
  background: #f1f5f9;
  padding: 8px 12px;
  border-radius: 6px;
  display: block;
  word-break: break-all;
  color: #334155;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 8px;
`;

const ActivityIcon = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

const ActivityDetails = styled.div`
  flex: 1;
`;

const ActivityTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
`;

const ActivityTime = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const SecuritySection = styled.div`
  background: #f8fafc;
  border-radius: 8px;
  padding: 20px;
`;

const SecurityItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e2e8f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const SecurityLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  span:first-child {
    font-size: 18px;
  }
`;

const SecurityStatus = styled.span<{ $enabled?: boolean }>`
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.$enabled ? '#dcfce7' : '#fef3c7'};
  color: ${props => props.$enabled ? '#166534' : '#92400e'};
`;

const ActionButton = styled.button`
  background: transparent;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }
`;

const SessionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SessionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

const SessionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SessionIcon = styled.span`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;

const SessionDetails = styled.div`
  flex: 1;
`;

const SessionDevice = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
`;

const SessionMeta = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const CurrentBadge = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  background: #dcfce7;
  color: #166534;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
`;

interface UserInfo {
  userId?: string;
  name?: string;
  email?: string;
  role?: string;
  tenantId?: string;
  phone?: string;
  mobile?: string;
  lastLoginAt?: string;
  passwordLastChanged?: string;
  twoFactorEnabled?: boolean;
}

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface EmailChangeForm {
  newEmail: string;
  password: string;
  otp: string;
}

interface MobileChangeForm {
  countryCode: string;
  newMobile: string;
  password: string;
  otp: string;
}

export const UserProfile: React.FC = () => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState<AdminSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  
  // Form states
  const [passwordForm, setPasswordForm] = useState<PasswordForm>({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [emailForm, setEmailForm] = useState<EmailChangeForm>({ newEmail: '', password: '', otp: '' });
  const [mobileForm, setMobileForm] = useState<MobileChangeForm>({ countryCode: '+91', newMobile: '', password: '', otp: '' });
  const [emailVerificationId, setEmailVerificationId] = useState<string | null>(null);
  const [mobileVerificationId, setMobileVerificationId] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const loadProfile = useCallback(async () => {
    try {
      const profile = await adminApi.getProfile();
      const storedUser = localStorage.getItem('userInfo');
      const localUser = storedUser ? JSON.parse(storedUser) : {};
      setUserInfo({ ...localUser, ...profile });
    } catch (err) {
      // Fallback to localStorage
      const storedUser = localStorage.getItem('userInfo');
      if (storedUser) {
        try {
          setUserInfo(JSON.parse(storedUser));
        } catch (e) {
          console.error('Failed to parse user info');
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const activeSessions = await adminApi.getActiveSessions();
      setSessions(activeSessions);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const getUserInitials = () => {
    if (userInfo?.name) {
      return userInfo.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (userInfo?.email) {
      return userInfo.email[0].toUpperCase();
    }
    return 'A';
  };

  const getDisplayName = () => {
    if (userInfo?.name) return userInfo.name;
    if (userInfo?.email) return userInfo.email.split('@')[0];
    return 'Admin User';
  };

  // Password change handler
  const handlePasswordChange = async () => {
    setFormError(null);
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setFormError('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }
    setFormLoading(true);
    try {
      await adminApi.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword,
      });
      setSuccess('Password changed successfully');
      setShowPasswordModal(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      loadProfile();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setFormLoading(false);
    }
  };

  // Email change handlers
  const handleRequestEmailChange = async () => {
    setFormError(null);
    if (!emailForm.newEmail || !emailForm.newEmail.includes('@')) {
      setFormError('Please enter a valid email address');
      return;
    }
    if (!emailForm.password) {
      setFormError('Please enter your current password to verify');
      return;
    }
    setFormLoading(true);
    try {
      const response = await adminApi.requestEmailChange({ 
        newEmail: emailForm.newEmail,
        password: emailForm.password 
      });
      if (response.verificationId) {
        setEmailVerificationId(response.verificationId);
      }
      setSuccess('Verification code sent to your new email');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to send verification code');
    } finally {
      setFormLoading(false);
    }
  };

  const handleVerifyEmailChange = async () => {
    setFormError(null);
    if (!emailForm.otp || emailForm.otp.length < 4) {
      setFormError('Please enter the verification code');
      return;
    }
    if (!emailVerificationId) {
      setFormError('Please request a verification code first');
      return;
    }
    setFormLoading(true);
    try {
      await adminApi.verifyEmailChange({
        otp: emailForm.otp,
        verificationId: emailVerificationId,
      });
      setSuccess('Email address updated successfully');
      setShowEmailModal(false);
      setEmailForm({ newEmail: '', password: '', otp: '' });
      setEmailVerificationId(null);
      loadProfile();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to verify email change');
    } finally {
      setFormLoading(false);
    }
  };

  // Mobile change handlers
  const handleRequestMobileChange = async () => {
    setFormError(null);
    if (!mobileForm.newMobile || mobileForm.newMobile.length < 10) {
      setFormError('Please enter a valid mobile number');
      return;
    }
    if (!mobileForm.password) {
      setFormError('Please enter your current password to verify');
      return;
    }
    setFormLoading(true);
    try {
      const response = await adminApi.requestMobileChange({ 
        countryCode: mobileForm.countryCode,
        newMobile: mobileForm.newMobile,
        password: mobileForm.password 
      });
      if (response.verificationId) {
        setMobileVerificationId(response.verificationId);
      }
      setSuccess('Verification code sent to your new mobile number');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to send verification code');
    } finally {
      setFormLoading(false);
    }
  };

  const handleVerifyMobileChange = async () => {
    setFormError(null);
    if (!mobileForm.otp || mobileForm.otp.length < 4) {
      setFormError('Please enter the verification code');
      return;
    }
    if (!mobileVerificationId) {
      setFormError('Please request a verification code first');
      return;
    }
    setFormLoading(true);
    try {
      await adminApi.verifyMobileChange({
        otp: mobileForm.otp,
        verificationId: mobileVerificationId,
      });
      setSuccess('Mobile number updated successfully');
      setShowMobileModal(false);
      setMobileForm({ countryCode: '+91', newMobile: '', password: '', otp: '' });
      setMobileVerificationId(null);
      loadProfile();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to verify mobile change');
    } finally {
      setFormLoading(false);
    }
  };

  // Session handlers
  const handleViewSessions = () => {
    setShowSessionsModal(true);
    loadSessions();
  };

  const handleTerminateSession = async (sessionId: string) => {
    try {
      await adminApi.terminateSession(sessionId);
      setSuccess('Session terminated successfully');
      loadSessions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to terminate session');
    }
  };

  const handleLogoutAllOthers = async () => {
    const currentSessionId = localStorage.getItem('sessionId');
    if (!currentSessionId) return;
    try {
      await adminApi.logoutAllOtherSessions(currentSessionId);
      setSuccess('All other sessions terminated');
      loadSessions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to terminate sessions');
    }
  };

  const getDeviceIcon = (userAgent?: string) => {
    if (!userAgent) return '💻';
    const ua = userAgent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) return '📱';
    if (ua.includes('tablet') || ua.includes('ipad')) return '📱';
    if (ua.includes('mac')) return '🖥️';
    if (ua.includes('windows')) return '💻';
    if (ua.includes('linux')) return '🐧';
    return '💻';
  };

  const formatSessionTime = (timestamp?: string) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Clear success/error messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (loading) {
    return (
      <AdminShell title="User Profile">
        <LoadingSpinner>Loading user information...</LoadingSpinner>
      </AdminShell>
    );
  }

  return (
    <AdminShell title="User Profile">
      <PageContainer>
        {success && <AlertMessage $variant="success">{success}</AlertMessage>}
        {error && <AlertMessage $variant="error">{error}</AlertMessage>}
        
        <Card>
          <ProfileHeader>
            <AvatarLarge>{getUserInitials()}</AvatarLarge>
            <ProfileInfo>
              <ProfileName>{getDisplayName()}</ProfileName>
              <ProfileEmail>{userInfo?.email || 'No email configured'}</ProfileEmail>
              <RoleBadge>{userInfo?.role || 'ADMIN'}</RoleBadge>
            </ProfileInfo>
          </ProfileHeader>
          
          <CardBody>
            <Section>
              <SectionTitle>
                <span>👤</span>
                Account Information
              </SectionTitle>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>User ID</InfoLabel>
                  <MonoValue>{userInfo?.userId || 'N/A'}</MonoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Email Address</InfoLabel>
                  <InfoValue style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {userInfo?.email || 'Not configured'}
                    <ActionButton onClick={() => setShowEmailModal(true)}>Change</ActionButton>
                  </InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Full Name</InfoLabel>
                  <InfoValue>{userInfo?.name || 'Not configured'}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Phone Number</InfoLabel>
                  <InfoValue style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {userInfo?.phone || userInfo?.mobile || 'Not configured'}
                    <ActionButton onClick={() => setShowMobileModal(true)}>Change</ActionButton>
                  </InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Role</InfoLabel>
                  <InfoValue>{userInfo?.role || 'ADMIN'}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Tenant ID</InfoLabel>
                  <MonoValue>{userInfo?.tenantId || 'N/A'}</MonoValue>
                </InfoItem>
              </InfoGrid>
            </Section>
            
            <Section>
              <SectionTitle>
                <span>🔐</span>
                Security Settings
              </SectionTitle>
              <SecuritySection>
                <SecurityItem>
                  <SecurityLabel>
                    <span>🔑</span>
                    <div>
                      <div style={{ fontWeight: 500, color: '#1e293b' }}>Password</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        {userInfo?.passwordLastChanged 
                          ? `Last changed: ${new Date(userInfo.passwordLastChanged).toLocaleDateString('en-IN')}`
                          : 'Secure password set'}
                      </div>
                    </div>
                  </SecurityLabel>
                  <ActionButton onClick={() => setShowPasswordModal(true)}>Change Password</ActionButton>
                </SecurityItem>
                <SecurityItem>
                  <SecurityLabel>
                    <span>📱</span>
                    <div>
                      <div style={{ fontWeight: 500, color: '#1e293b' }}>Two-Factor Authentication</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>Add extra security to your account</div>
                    </div>
                  </SecurityLabel>
                  <SecurityStatus $enabled={userInfo?.twoFactorEnabled}>
                    {userInfo?.twoFactorEnabled ? 'Enabled' : 'Not Enabled'}
                  </SecurityStatus>
                </SecurityItem>
                <SecurityItem>
                  <SecurityLabel>
                    <span>🌐</span>
                    <div>
                      <div style={{ fontWeight: 500, color: '#1e293b' }}>Active Sessions</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>Manage your active login sessions</div>
                    </div>
                  </SecurityLabel>
                  <ActionButton onClick={handleViewSessions}>View Sessions</ActionButton>
                </SecurityItem>
              </SecuritySection>
            </Section>
            
            <Section>
              <SectionTitle>
                <span>📊</span>
                Recent Activity
              </SectionTitle>
              <ActivityList>
                <ActivityItem>
                  <ActivityIcon>🔓</ActivityIcon>
                  <ActivityDetails>
                    <ActivityTitle>Logged in to Admin Dashboard</ActivityTitle>
                    <ActivityTime>
                      {userInfo?.lastLoginAt 
                        ? new Date(userInfo.lastLoginAt).toLocaleString('en-IN')
                        : 'Current session'}
                    </ActivityTime>
                  </ActivityDetails>
                </ActivityItem>
                <ActivityItem>
                  <ActivityIcon>📊</ActivityIcon>
                  <ActivityDetails>
                    <ActivityTitle>Dashboard Access</ActivityTitle>
                    <ActivityTime>Admin Portal • Active session</ActivityTime>
                  </ActivityDetails>
                </ActivityItem>
              </ActivityList>
            </Section>
          </CardBody>
        </Card>

        {/* Password Change Modal */}
        <ModalOverlay $show={showPasswordModal} onClick={() => setShowPasswordModal(false)}>
          <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Change Password</ModalTitle>
              <CloseButton onClick={() => setShowPasswordModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              {formError && <AlertMessage $variant="error">{formError}</AlertMessage>}
              <FormGroup>
                <Label>Current Password</Label>
                <Input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                  placeholder="Enter current password"
                />
              </FormGroup>
              <FormGroup>
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                  placeholder="Enter new password (min 8 characters)"
                />
              </FormGroup>
              <FormGroup>
                <Label>Confirm New Password</Label>
                <Input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Confirm new password"
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <SecondaryButton onClick={() => setShowPasswordModal(false)}>Cancel</SecondaryButton>
              <PrimaryButton onClick={handlePasswordChange} disabled={formLoading}>
                {formLoading ? 'Changing...' : 'Change Password'}
              </PrimaryButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>

        {/* Email Change Modal */}
        <ModalOverlay $show={showEmailModal} onClick={() => setShowEmailModal(false)}>
          <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Change Email Address</ModalTitle>
              <CloseButton onClick={() => setShowEmailModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              {formError && <AlertMessage $variant="error">{formError}</AlertMessage>}
              <FormGroup>
                <Label>New Email Address</Label>
                <Input
                  type="email"
                  value={emailForm.newEmail}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setEmailForm(prev => ({ ...prev, newEmail: e.target.value }))}
                  placeholder="Enter new email address"
                  disabled={!!emailVerificationId}
                />
              </FormGroup>
              {!emailVerificationId && (
                <FormGroup>
                  <Label>Current Password</Label>
                  <Input
                    type="password"
                    value={emailForm.password}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setEmailForm(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Enter your current password to verify"
                  />
                </FormGroup>
              )}
              {emailVerificationId && (
                <FormGroup>
                  <Label>Verification Code</Label>
                  <Input
                    type="text"
                    value={emailForm.otp}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setEmailForm(prev => ({ ...prev, otp: e.target.value }))}
                    placeholder="Enter the code sent to your new email"
                  />
                </FormGroup>
              )}
            </ModalBody>
            <ModalFooter>
              <SecondaryButton onClick={() => {
                setShowEmailModal(false);
                setEmailForm({ newEmail: '', password: '', otp: '' });
                setEmailVerificationId(null);
                setFormError(null);
              }}>Cancel</SecondaryButton>
              {!emailVerificationId ? (
                <PrimaryButton onClick={handleRequestEmailChange} disabled={formLoading}>
                  {formLoading ? 'Sending...' : 'Send Verification Code'}
                </PrimaryButton>
              ) : (
                <PrimaryButton onClick={handleVerifyEmailChange} disabled={formLoading}>
                  {formLoading ? 'Verifying...' : 'Verify & Update'}
                </PrimaryButton>
              )}
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>

        {/* Mobile Change Modal */}
        <ModalOverlay $show={showMobileModal} onClick={() => setShowMobileModal(false)}>
          <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Change Mobile Number</ModalTitle>
              <CloseButton onClick={() => setShowMobileModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              {formError && <AlertMessage $variant="error">{formError}</AlertMessage>}
              <FormGroup>
                <Label>New Mobile Number</Label>
                <Input
                  type="tel"
                  value={mobileForm.newMobile}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setMobileForm(prev => ({ ...prev, newMobile: e.target.value }))}
                  placeholder="Enter new mobile number"
                  disabled={!!mobileVerificationId}
                />
              </FormGroup>
              {!mobileVerificationId && (
                <FormGroup>
                  <Label>Current Password</Label>
                  <Input
                    type="password"
                    value={mobileForm.password}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setMobileForm(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Enter your current password to verify"
                  />
                </FormGroup>
              )}
              {mobileVerificationId && (
                <FormGroup>
                  <Label>Verification Code</Label>
                  <Input
                    type="text"
                    value={mobileForm.otp}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setMobileForm(prev => ({ ...prev, otp: e.target.value }))}
                    placeholder="Enter the code sent to your new mobile"
                  />
                </FormGroup>
              )}
            </ModalBody>
            <ModalFooter>
              <SecondaryButton onClick={() => {
                setShowMobileModal(false);
                setMobileForm({ countryCode: '+91', newMobile: '', password: '', otp: '' });
                setMobileVerificationId(null);
                setFormError(null);
              }}>Cancel</SecondaryButton>
              {!mobileVerificationId ? (
                <PrimaryButton onClick={handleRequestMobileChange} disabled={formLoading}>
                  {formLoading ? 'Sending...' : 'Send Verification Code'}
                </PrimaryButton>
              ) : (
                <PrimaryButton onClick={handleVerifyMobileChange} disabled={formLoading}>
                  {formLoading ? 'Verifying...' : 'Verify & Update'}
                </PrimaryButton>
              )}
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>

        {/* Sessions Modal */}
        <ModalOverlay $show={showSessionsModal} onClick={() => setShowSessionsModal(false)}>
          <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <ModalHeader>
              <ModalTitle>Active Sessions</ModalTitle>
              <CloseButton onClick={() => setShowSessionsModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              {sessionsLoading ? (
                <LoadingSpinner>Loading sessions...</LoadingSpinner>
              ) : sessions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                  No active sessions found
                </div>
              ) : (
                <SessionList>
                  {sessions.map((session) => {
                    const isCurrentSession = session.sessionId === localStorage.getItem('sessionId');
                    return (
                      <SessionItem key={session.sessionId}>
                        <SessionInfo>
                          <SessionIcon>{getDeviceIcon(session.userAgent)}</SessionIcon>
                          <SessionDetails>
                            <SessionDevice>
                              {session.deviceInfo || 'Unknown Device'}
                              {isCurrentSession && <CurrentBadge>Current</CurrentBadge>}
                            </SessionDevice>
                            <SessionMeta>
                              {session.ipAddress || 'Unknown IP'} • {session.location || 'Unknown Location'}
                            </SessionMeta>
                            <SessionMeta>
                              Last active: {formatSessionTime(session.lastActivityAt || session.createdAt)}
                            </SessionMeta>
                          </SessionDetails>
                        </SessionInfo>
                        {!isCurrentSession && (
                          <DangerButton 
                            onClick={() => handleTerminateSession(session.sessionId)}
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                          >
                            Terminate
                          </DangerButton>
                        )}
                      </SessionItem>
                    );
                  })}
                </SessionList>
              )}
            </ModalBody>
            <ModalFooter>
              <SecondaryButton onClick={() => setShowSessionsModal(false)}>Close</SecondaryButton>
              {sessions.length > 1 && (
                <DangerButton onClick={handleLogoutAllOthers}>
                  Logout All Other Sessions
                </DangerButton>
              )}
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      </PageContainer>
    </AdminShell>
  );
};

export default UserProfile;
